import { TruncatedTextWithTooltipWhenTruncated } from "@/components/data-display/TruncatedTextWithTooltipWhenTruncated";
import { Icon } from "@/components/icons/Icon";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import { ViewportAwareFixedMenuV2 } from "@/components/interactive/ViewportAwareFixedMenuV2";
import { classNames } from "@/utils/classnames";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { type LinkProps } from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";

export interface BreadcrumbV2SiblingItem {
  id: string;
  label: string;
  description?: string;
  metadata?: string;
  current?: boolean;
  /** De-emphasize destinations that are unavailable/inactive but still navigable. */
  inactive?: boolean;
  childrenMenuSubtitle?: string;
  children?: readonly BreadcrumbV2SiblingItem[];
  href?: LinkProps["href"];
  onClick?: () => void;
}

export interface BreadcrumbV2Item {
  id: string;
  label: string;
  current?: boolean;
  hasDropdown?: boolean;
  /** Informational hierarchy label: inactive color, no hover/click/dropdown affordance. */
  nonInteractive?: boolean;
  /** Optional title row shown at the top of this breadcrumb's dropdown menu. */
  menuSubtitle?: string;
  searchPlaceholder?: string;
  siblings?: readonly BreadcrumbV2SiblingItem[];
  href?: LinkProps["href"];
  onClick?: () => void;
}

export interface BreadcrumbsV2Props extends Omit<React.ComponentProps<"nav">, "children"> {
  items: readonly BreadcrumbV2Item[];
  showDetailPanes?: boolean;
}

const BREADCRUMB_TEXT = "text-cds-sm font-medium whitespace-nowrap";

const BREADCRUMB_ITEM_BASE =
  "inline-flex h-cds-16 min-w-0 max-w-full shrink-0 items-center gap-cds-4";

const BREADCRUMB_ITEM_TEXT =
  "text-cds-ink-subtle dark:!text-zinc-400 hover:text-cds-ink-secondary dark:hover:!text-zinc-300";

const BREADCRUMB_ITEM_NON_INTERACTIVE_TEXT = "text-cds-ink-subtle dark:!text-zinc-400";

const BREADCRUMB_ITEM_OPEN_TEXT = "text-cds-ink-secondary dark:!text-zinc-300";

const BREADCRUMB_CURRENT_TEXT = "!text-zinc-950 dark:!text-zinc-50";

const BREADCRUMB_SEPARATOR_TEXT = "!text-zinc-300 dark:!text-zinc-700";

const BREADCRUMB_SEARCH_THRESHOLD = 10;

const getBreadcrumbSiblingDescription = (sibling: BreadcrumbV2SiblingItem) =>
  sibling.description ?? `Open ${sibling.label} from this level of navigation.`;

const renderBreadcrumbSiblingLabel = (sibling: BreadcrumbV2SiblingItem): React.ReactNode =>
  sibling.inactive ? (
    <span className={"line-through opacity-60"}>{sibling.label}</span>
  ) : (
    sibling.label
  );

const breadcrumbSiblingMatchesSearch = (sibling: BreadcrumbV2SiblingItem, query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  return (
    sibling.label.toLowerCase().includes(normalizedQuery) ||
    sibling.metadata?.toLowerCase().includes(normalizedQuery)
  );
};

const BreadcrumbContent: React.FC<{
  item: BreadcrumbV2Item;
  chevronShifted: boolean;
  showDropdown: boolean;
}> = ({ item, chevronShifted, showDropdown }) => {
  return (
    <>
      <TruncatedTextWithTooltipWhenTruncated className={"relative z-[1]"} value={item.label} />
      {showDropdown && (
        <Icon
          name={"chevronDown"}
          size={"sm"}
          aria-hidden={true}
          className={classNames(
            "relative z-[1] shrink-0 transition-[transform] duration-200 ease-out will-change-transform",
            chevronShifted ? "translate-y-px" : "translate-y-0",
          )}
        />
      )}
    </>
  );
};

export const BreadcrumbsV2 = React.forwardRef<HTMLElement, BreadcrumbsV2Props>(
  function BreadcrumbsV2Impl({ items, showDetailPanes = true, className, ...rest }, ref) {
    const rootRef = useRef<HTMLElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const closeTimerRef = useRef<number | null>(null);
    const [openItemId, setOpenItemId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{
      left: number;
      top: number;
    } | null>(null);
    const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
    const [previewSibling, setPreviewSibling] = useState<BreadcrumbV2SiblingItem | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    const clearCloseTimer = useCallback(() => {
      if (closeTimerRef.current == null) return;

      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }, []);

    const closeDropdown = useCallback(() => {
      clearCloseTimer();
      setOpenItemId(null);
      setDropdownPosition(null);
      setPreviewSibling(null);
      setSearchQuery("");
    }, [clearCloseTimer]);

    const queueCloseDropdown = useCallback(() => {
      clearCloseTimer();
      closeTimerRef.current = window.setTimeout(closeDropdown, 120);
    }, [clearCloseTimer, closeDropdown]);

    const showSiblingPreview = useCallback(
      (sibling: BreadcrumbV2SiblingItem) => {
        if (!showDetailPanes) return;

        setPreviewSibling(sibling);
      },
      [showDetailPanes],
    );

    const renderSiblingMenuItem = useCallback(
      (sibling: BreadcrumbV2SiblingItem) => {
        const siblingChildren = sibling.children ?? [];
        // --submenu-available-height variable comes from MenuItemV2.tsx; it isn't a global CSS var
        const submenu =
          siblingChildren.length > 0 ? (
            <MenuContainerV2
              className={
                "w-cds-dropdown max-h-[var(--submenu-available-height,var(--viewport-aware-fixed-menu-max-height))] overflow-y-auto"
              }
            >
              <MenuSectionV2>
                {sibling.childrenMenuSubtitle != null && (
                  <MenuItemV2
                    size={"sm"}
                    variant={"title"}
                    label={sibling.childrenMenuSubtitle}
                    showBottomDivider
                  />
                )}
                {siblingChildren.map((child) => (
                  <MenuItemV2
                    key={child.id}
                    variant={child.metadata != null ? "two-line" : "default"}
                    size={"sm"}
                    label={renderBreadcrumbSiblingLabel(child)}
                    subtitle={child.metadata}
                    active={child.current}
                    style={child.current ? { cursor: "default" } : undefined}
                    onMouseEnter={() => showSiblingPreview(child)}
                    onFocus={() => showSiblingPreview(child)}
                    href={child.current ? undefined : child.href}
                    role={"menuitem"}
                    onClick={() => {
                      if (!child.current) {
                        child.onClick?.();
                      }
                      closeDropdown();
                    }}
                  />
                ))}
              </MenuSectionV2>
            </MenuContainerV2>
          ) : undefined;
        const siblingIsCurrentLeaf = sibling.current === true && siblingChildren.length === 0;

        return (
          <MenuItemV2
            key={sibling.id}
            variant={sibling.metadata != null ? "two-line" : "default"}
            size={"sm"}
            label={renderBreadcrumbSiblingLabel(sibling)}
            subtitle={sibling.metadata}
            active={sibling.current && siblingChildren.length === 0}
            submenu={submenu}
            style={siblingIsCurrentLeaf ? { cursor: "default" } : undefined}
            onMouseEnter={() => showSiblingPreview(sibling)}
            onFocus={() => showSiblingPreview(sibling)}
            href={siblingIsCurrentLeaf ? undefined : sibling.href}
            role={"menuitem"}
            onClick={() => {
              if (!siblingIsCurrentLeaf) {
                sibling.onClick?.();
              }
              closeDropdown();
            }}
          />
        );
      },
      [closeDropdown, showSiblingPreview],
    );

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        rootRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    useEffect(() => {
      if (!showDetailPanes) {
        setPreviewSibling(null);
      }
    }, [showDetailPanes]);

    useEffect(() => clearCloseTimer, [clearCloseTimer]);

    useEffect(() => {
      if (!openItemId) return;

      const openItem = items.find((item) => item.id === openItemId);
      const siblingCount = openItem?.siblings?.length ?? 0;
      const searchable = siblingCount >= BREADCRUMB_SEARCH_THRESHOLD;

      if (searchable) {
        searchInputRef.current?.focus();
      }
    }, [items, openItemId]);

    useEffect(() => {
      if (!openItemId) return;

      const handlePointerDown = (event: PointerEvent) => {
        if (!rootRef.current?.contains(event.target as Node)) {
          closeDropdown();
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          closeDropdown();
        }
      };

      document.addEventListener("pointerdown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("pointerdown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [closeDropdown, openItemId]);

    return (
      <nav
        ref={setRefs}
        aria-label={"Breadcrumb"}
        data-ds-v2={""}
        className={classNames(
          "flex h-cds-40 min-w-0 items-center gap-cds-8 overflow-hidden p-cds-12",
          BREADCRUMB_TEXT,
          className,
        )}
        {...rest}
      >
        <ol className={"flex min-w-0 items-center gap-cds-8"}>
          {items.map((item, index) => {
            const nonInteractive = item.nonInteractive === true;
            const current = nonInteractive ? false : item.current ?? index === items.length - 1;
            const hasDropdownMenu =
              !nonInteractive && Boolean(item.hasDropdown && item.siblings?.length);
            const interactive =
              !nonInteractive &&
              (hasDropdownMenu || (!current && (item.href != null || item.onClick != null)));
            const visuallyNonInteractive = nonInteractive || (!current && !interactive);
            const open = openItemId === item.id;
            const siblings = item.siblings ?? [];
            const searchable = siblings.length >= BREADCRUMB_SEARCH_THRESHOLD;
            const hasMenuHeader = item.menuSubtitle != null || searchable;
            const normalizedSearchQuery = searchQuery.trim().toLowerCase();
            const visibleSiblings =
              searchable && normalizedSearchQuery
                ? siblings.filter((sibling) =>
                    breadcrumbSiblingMatchesSearch(sibling, normalizedSearchQuery),
                  )
                : siblings;
            const visibleSiblingsHaveSubmenus = visibleSiblings.some(
              (sibling) => (sibling.children?.length ?? 0) > 0,
            );
            const scrollsWithinMenu = !visibleSiblingsHaveSubmenus;
            const chevronShifted = open || hoveredItemId === item.id;
            const itemClasses = classNames(
              BREADCRUMB_ITEM_BASE,
              visuallyNonInteractive
                ? BREADCRUMB_ITEM_NON_INTERACTIVE_TEXT
                : current
                  ? BREADCRUMB_CURRENT_TEXT
                  : open
                    ? BREADCRUMB_ITEM_OPEN_TEXT
                    : BREADCRUMB_ITEM_TEXT,
              interactive ? "cursor-pointer" : "cursor-default",
            );

            return (
              <React.Fragment key={item.id}>
                {index > 0 && (
                  <li
                    aria-hidden={true}
                    className={classNames("shrink-0 cursor-default", BREADCRUMB_SEPARATOR_TEXT)}
                  >
                    /
                  </li>
                )}
                <li
                  className={"relative min-w-0 shrink-0 last:shrink"}
                  onMouseEnter={clearCloseTimer}
                  onMouseLeave={() => {
                    if (open) queueCloseDropdown();
                  }}
                >
                  {interactive && !hasDropdownMenu && item.href != null ? (
                    <ChalkLink
                      unstyled
                      href={item.href}
                      className={classNames(
                        "group/breadcrumb relative appearance-none border-0 bg-transparent p-0",
                        itemClasses,
                      )}
                      onMouseEnter={() => setHoveredItemId(item.id)}
                      onMouseLeave={() =>
                        setHoveredItemId((prev) => (prev === item.id ? null : prev))
                      }
                      aria-current={current ? "page" : undefined}
                      onClick={() => {
                        if (!current) {
                          item.onClick?.();
                        }
                        closeDropdown();
                      }}
                    >
                      <span
                        aria-hidden={true}
                        data-breadcrumb-hover-surface={""}
                        className={
                          "pointer-events-auto absolute -inset-x-cds-6 -inset-y-cds-4 z-0 rounded-cds-4"
                        }
                      />
                      <BreadcrumbContent
                        item={item}
                        chevronShifted={chevronShifted}
                        showDropdown={false}
                      />
                    </ChalkLink>
                  ) : interactive ? (
                    <button
                      type={"button"}
                      className={classNames(
                        "group/breadcrumb relative appearance-none border-0 bg-transparent p-0",
                        itemClasses,
                      )}
                      onMouseEnter={() => setHoveredItemId(item.id)}
                      onMouseLeave={() =>
                        setHoveredItemId((prev) => (prev === item.id ? null : prev))
                      }
                      aria-haspopup={hasDropdownMenu ? "menu" : undefined}
                      aria-expanded={hasDropdownMenu ? open : undefined}
                      aria-current={current ? "page" : undefined}
                      onClick={(event) => {
                        if (!current) {
                          item.onClick?.();
                        }
                        if (hasDropdownMenu) {
                          const rect = event.currentTarget.getBoundingClientRect();
                          clearCloseTimer();
                          setSearchQuery("");
                          setPreviewSibling(null);
                          setOpenItemId((prev) => {
                            if (prev === item.id) {
                              setDropdownPosition(null);
                              return null;
                            }

                            setDropdownPosition({
                              left: rect.left,
                              top: rect.bottom + 4,
                            });
                            return item.id;
                          });
                        } else {
                          closeDropdown();
                        }
                      }}
                    >
                      <span
                        aria-hidden={true}
                        data-breadcrumb-hover-surface={""}
                        className={
                          "pointer-events-auto absolute -inset-x-cds-6 -inset-y-cds-4 z-0 rounded-cds-4"
                        }
                      />
                      <BreadcrumbContent
                        item={item}
                        chevronShifted={chevronShifted}
                        showDropdown={hasDropdownMenu}
                      />
                    </button>
                  ) : (
                    <span className={itemClasses} aria-current={current ? "page" : undefined}>
                      <BreadcrumbContent item={item} chevronShifted={false} showDropdown={false} />
                    </span>
                  )}
                  {hasDropdownMenu && open && dropdownPosition != null && (
                    <ViewportAwareFixedMenuV2
                      className={"flex items-stretch gap-cds-4"}
                      left={dropdownPosition.left}
                      top={dropdownPosition.top}
                      onMouseEnter={clearCloseTimer}
                      onMouseLeave={queueCloseDropdown}
                    >
                      <MenuContainerV2
                        className={classNames(
                          "w-cds-dropdown",
                          // MenuItemV2 flyouts are absolute descendants, so a
                          // scrollable parent would clip nested breadcrumb menus.
                          scrollsWithinMenu &&
                            "max-h-[var(--viewport-aware-fixed-menu-max-height)] overflow-y-auto",
                        )}
                      >
                        <MenuSectionV2>
                          {hasMenuHeader &&
                            (searchable ? (
                              <MenuItemV2
                                size={"sm"}
                                variant={"search"}
                                label={item.searchPlaceholder ?? "Search"}
                                searchValue={searchQuery}
                                onSearchChange={(value) => {
                                  setSearchQuery(value);
                                  setPreviewSibling(null);
                                }}
                                searchInputRef={searchInputRef}
                                hideSearchIcon
                                showBottomDivider
                              />
                            ) : (
                              item.menuSubtitle != null && (
                                <MenuItemV2
                                  size={"sm"}
                                  variant={"title"}
                                  label={item.menuSubtitle}
                                  showBottomDivider
                                />
                              )
                            ))}
                          {visibleSiblings.length > 0 ? (
                            visibleSiblings.map((sibling) => renderSiblingMenuItem(sibling))
                          ) : (
                            <MenuItemV2 size={"sm"} variant={"title"} label={"No results"} />
                          )}
                        </MenuSectionV2>
                      </MenuContainerV2>
                      {showDetailPanes && previewSibling && (
                        <BreadcrumbSiblingPreviewPane sibling={previewSibling} />
                      )}
                    </ViewportAwareFixedMenuV2>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    );
  },
);

const BreadcrumbSiblingPreviewPane: React.FC<{ sibling: BreadcrumbV2SiblingItem }> = ({
  sibling,
}) => {
  return (
    <aside
      role={"tooltip"}
      aria-label={`${sibling.label} preview`}
      className={classNames(
        "flex w-cds-dropdown self-stretch flex-col overflow-hidden rounded-cds-6 border-[1px]",
        "border-cds-surface-border bg-cds-surface-default p-cds-10 shadow-cds-m",
        "dark:border-white/[0.08] dark:bg-cds-elevated-dark dark:shadow-cds-m-dark",
      )}
    >
      <div className={"min-w-0 truncate text-cds-md font-normal text-cds-ink dark:text-zinc-50"}>
        {sibling.label}
      </div>
      <p className={"mt-cds-6 text-cds-sm text-cds-ink-muted dark:!text-zinc-400"}>
        {getBreadcrumbSiblingDescription(sibling)}
      </p>
      <dl className={"mt-cds-8 flex items-center justify-between gap-cds-8 text-cds-xs"}>
        <dt className={"text-cds-ink-subtle dark:!text-zinc-500"}>Type</dt>
        <dd className={"truncate font-medium text-cds-ink-secondary dark:!text-zinc-300"}>
          {sibling.current ? "Current page" : "Sibling page"}
        </dd>
      </dl>
    </aside>
  );
};
